<script>
  import DialogBase from "../../../components/dialog/DialogBase.svelte";
  import { translate } from "../../../i18n/i18next.js";

  import DialogSplitter from "../../../components/dialog/DialogSplitter.svelte";
  import GeneralButton from "../../../components/button/GeneralButton.svelte";
  import GeneralSelect from "../../../components/select/GeneralSelect.svelte";
  import { getFormattedDateTimeLocalized } from "../../../utils/time.js";
  import {
    getDispatchingViewConfiguration
  } from "../../../utils/configurationStorage/views/DispatchingViewConfigurationStorage.js";
  import SearchInput from "../../../components/input/SearchInput.svelte";

  export let projectId;
  export let dialog;
  export let onClose;
  export let userId;

  export let resources = [];
  export let resourceGroups = [];
  export let resourceObjectID = "";

  export let onMoveToResource

  let selectedResourceObjectID = resourceObjectID;
  let searchText = "";
  let selectedSortOption = "viewDate";

  async function onOk() {
    onClose();
    await onMoveToResource(selectedResourceObjectID)
  }

  let sortDescending = true;
  let options = {
    viewDate: translate("frontend:views.dispatchingView.lastViewedDay"),
    displayOrder: translate("asprova.terms.displayOrder"),
    code: translate("asprova.terms.resourceCode"),
    name: translate("asprova.terms.resourceName"),
  };
  $: selectedGroup = "";

  $: getFilteredAndSortedResources = () => {
    return handledData
      .filter(
        (r) =>
          selectedGroup === "" || (r.group && r.group.filter(group => group.code == selectedGroup).length)
      )
      .filter(
        (r) =>
          r.name.toLowerCase().includes(searchText.toLowerCase()) ||
          r.code.toLowerCase().includes(searchText.toLowerCase())
      )
      .sort((a, b) => {
        const ret = sortDescending ? 1 : -1;

        if (!a[selectedSortOption] && !b[selectedSortOption]) return 0;
        if (!a[selectedSortOption]) return ret;
        if (!b[selectedSortOption]) return -ret;

        if (selectedSortOption === "viewDate") {
          const dateA = new Date(a.viewDate);
          const dateB = new Date(b.viewDate);
          if (dateA === dateB) return 0;
          if (dateA < dateB) return ret;
          return -ret;
        }

        if (b[selectedSortOption] > a[selectedSortOption]) return ret;
        if (b[selectedSortOption] < a[selectedSortOption]) return -ret;
        return 0;
      });
  };

  function toggleSortOrder() {
    sortDescending = !sortDescending;
  }

  let handledData = [];
  $: if (resources) {
    handledData = [];
    let resourceCodeToGroupMap = {};
    resourceGroups.forEach(group => {
      group.groupMembers.forEach((resource) => {
        if (!resourceCodeToGroupMap[resource.code]) {
          // Note that a resource can belong to multiple groups
          resourceCodeToGroupMap[resource.code] = [];
        }
        resourceCodeToGroupMap[resource.code].push(group);
      });
    });

    const savedSettings = getDispatchingViewConfiguration(userId, projectId);

    let resourceObjectIDtoViewDateMap = {};
    savedSettings?.viewDates?.forEach((entry) => {
      if (entry.viewDate) {
        resourceObjectIDtoViewDateMap[entry.resourceObjectID] =
          getFormattedDateTimeLocalized(new Date(entry.viewDate), true);
      }
    });

    resources.forEach((resource) => {
      handledData.push({
        code: resource.code,
        name: resource.name,
        displayOrder: resource.displayOrder,
        objectID: resource.objectId,
        group: resourceCodeToGroupMap[resource.code],
        viewDate: resourceObjectIDtoViewDateMap?.[resource.objectId],
      });
    });
  }
</script>

<DialogBase
  bind:dialog
  extraStyle="display: flex; flex-direction: column; min-width: 700px; z-index: 100"
>
  <div class="top-panel">
    <div class="top-panel-title">
      <span>
        {translate("frontend:views.dispatchingView.resourceSelection")}
      </span>
    </div>
    <div class="top-panel-filter">
      <span>
        {translate("asprova.terms.resourceGroup")}:
      </span>
      <GeneralSelect
        extraStyle="margin-left: 10px; border: solid 2px gray;"
        options={resourceGroups.map((group) => ({
          text: group.code,
          value: group.code,
        }))}
        bind:value={selectedGroup}
        defaultValue={""}
        defaultText={translate("generic.unspecified")}
      />
    </div>
  </div>
  <DialogSplitter />
  <div class="control-row">
    <SearchInput
      bind:value={searchText}
      placeholder={translate(
        "frontend:views.dispatchingView.searchByResourceCodeOrResourceName"
      )}
      width="90%"
    />
    <div style="flex-grow: 1;"></div>
    <GeneralSelect
      extraStyle="margin-right: 10px; border: none; color: #00468f; font-weight:bold;"
      options={Object.entries(options).map(([key, text]) => ({
        text: text,
        value: key,
      }))}
      bind:value={selectedSortOption}
    />
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <img
      src={sortDescending
        ? "/assets/icon_order_descending.svg"
        : "/assets/icon_order_ascending.svg"}
      alt=""
      on:click={toggleSortOrder}
      style="cursor: pointer; margin-right: 2px;"
    />
  </div>
  <div class="table-container">
    <table>
      <thead>
        <tr>
          <th />
          <th>{translate("asprova.terms.resourceCode")}</th>
          <th>{translate("asprova.terms.resourceName")}</th>
          <th>
            {translate("frontend:views.dispatchingView.lastViewedDay")}
          </th>
        </tr>
      </thead>
      <tbody>
        {#each getFilteredAndSortedResources() as resource}
          <tr
            on:click={() => {
              selectedResourceObjectID = resource.objectID;
            }}
            class="table-row {resource.objectID == selectedResourceObjectID
              ? 'selected-row'
              : ''}"
          >
            <td class="check-cell"
              >{resource.objectID == selectedResourceObjectID ? "✓" : ""}</td
            >
            <td>{resource.code || "-"}</td>
            <td>{resource.name || "-"}</td>
            <td>{resource.viewDate || "-"}</td>
          </tr>
        {/each}
        {#if getFilteredAndSortedResources().length === 0}
          <tr>
            <td colspan="4" style="text-align:center"
              >{translate(
                "frontend:components.filterInput.noChoiceMatched"
              )}</td
            >
          </tr>
        {/if}
      </tbody>
    </table>
  </div>

  <DialogSplitter />
  <div class="bottom-controls">
    <GeneralButton
      buttonText={translate("generic.cancel")}
      buttonType="secondary"
      buttonFunction={onClose}
      buttonExtraStyle="min-width: 150px; min-height: 35px;"
    />
    <GeneralButton
      buttonText={"OK"}
      buttonFunction={onOk}
      buttonExtraStyle="min-width: 150px; min-height: 35px;"
    />
  </div>
</DialogBase>

<style>
  .top-panel {
    padding: 1rem 0.5rem;
    display: flex;
    align-items: center;
    justify-content: end;
    flex-wrap: wrap;
    gap: 1rem;
  }
  .top-panel-title {
    max-width: 100%;
    flex: 1;
    white-space: nowrap;
    overflow-wrap: anywhere;
    font-weight: bold;
    font-size: 1.5rem;
    color: gray;
  }
  .top-panel-filter {
    white-space: nowrap;
    text-align: right;
    color: black;
    font-size: 1rem;
  }
  .bottom-controls {
    display: flex;
    justify-content: end;
    align-items: center;
    gap: 2rem;
    padding: 1rem;
    font-size: 1.2rem;
  }
  .control-row {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 5px;
    box-sizing: border-box;
    margin-top: 6px;
  }
  .table-container {
    overflow-y: auto;
  }
  table {
    width: 100%;
    border-collapse: collapse;
  }
  th,
  td {
    border: 0;
    padding: 8px;
    text-align: left;
    min-width: 10px;
  }
  th {
    background-color: #f2f2f2;
    position: sticky;
    top: 0;
    z-index: 1;
  }
  .table-row:hover {
    background-color: #e0e0e0;
    cursor: pointer;
  }
  .check-cell {
    color: #00468f;
  }
  tr.selected-row {
    font-weight: bold;
  }
</style>
